<template>
  <div class="dimension-scores">
    <h3 class="title">七维度性格分析</h3>
    
    <!-- 雷达图容器 -->
    <div ref="containerRef" class="radar-chart-container">
      <svg :width="chartSize" :height="chartSize" :viewBox="`0 0 ${chartSize} ${chartSize}`" class="radar-chart" preserveAspectRatio="xMidYMid meet">
        <!-- 背景网格 -->
        <g v-for="level in 5" :key="'grid-' + level" class="grid-level">
          <polygon
            :points="getPolygonPoints(level / 5)"
            fill="none"
            :stroke="level === 5 ? '#cbd5e0' : '#e2e8f0'"
            :stroke-width="level === 5 ? 2 : 1"
          />
        </g>

        <!-- 轴线和标签 -->
        <g v-for="(dim, index) in dimensionList" :key="'axis-' + dim.key" class="axis-group">
          <!-- 轴线 -->
          <line
            :x1="centerX"
            :y1="centerY"
            :x2="getPointX(index, 1)"
            :y2="getPointY(index, 1)"
            stroke="#cbd5e0"
            stroke-width="1"
          />
          
          <!-- 标签 -->
          <g :transform="`translate(${getLabelX(index)}, ${getLabelY(index)})`">
            <text
              text-anchor="middle"
              dominant-baseline="middle"
              class="dimension-label"
              :fill="dim.color"
            >
              {{ dim.name }}
            </text>
            <text
              text-anchor="middle"
              dominant-baseline="middle"
              dy="16"
              class="dimension-score"
            >
              {{ formatScore(userScores[dim.key]) }}
            </text>
          </g>
        </g>

        <!-- 用户得分多边形 -->
        <polygon
          :points="getUserPolygonPoints()"
          :fill="primaryColor + '30'"
          :stroke="primaryColor"
          stroke-width="3"
          class="user-polygon"
        />

        <!-- 用户得分点 -->
        <g v-for="(dim, index) in dimensionList" :key="'point-' + dim.key" class="score-point">
          <circle
            :cx="getUserPointX(index)"
            :cy="getUserPointY(index)"
            r="5"
            :fill="primaryColor"
            stroke="white"
            stroke-width="2"
          />
        </g>
      </svg>
    </div>

    <!-- 维度详细说明 -->
    <div class="dimensions-list">
      <div 
        v-for="dim in dimensionList" 
        :key="dim.key"
        class="dimension-item"
      >
        <div class="dimension-header">
          <div class="dimension-info">
            <span class="dimension-icon" :style="{ backgroundColor: dim.color + '20', color: dim.color }">
              {{ dim.emoji }}
            </span>
            <div class="dimension-text">
              <div class="dimension-name-row">
                <span class="dimension-name-text">{{ dim.name }}</span>
                <span class="dimension-score-badge" :style="{ backgroundColor: dim.color + '20', color: dim.color }">
                  {{ formatScore(userScores[dim.key]) }}
                </span>
              </div>
              <div class="dimension-description">{{ dim.description }}</div>
            </div>
          </div>
        </div>
        
        <!-- 进度条 -->
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ 
              width: (userScores[dim.key] / maxScore * 100) + '%',
              backgroundColor: dim.color 
            }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { dimensions } from '@/data/animalQuestions'

const props = defineProps({
  userScores: {
    type: Object,
    required: true
  },
  maxScore: {
    type: Number,
    default: 10
  },
  size: {
    type: Number,
    default: 400
  },
  primaryColor: {
    type: String,
    default: '#8b5cf6'
  }
})

// 响应式尺寸计算
const chartSize = ref(props.size)
const containerRef = ref(null)

// 计算响应式尺寸
const calculateSize = () => {
  if (typeof window === 'undefined' || !containerRef.value) return
  
  const containerWidth = containerRef.value.clientWidth
  const padding = 60 // 为标签留出更多空间，防止溢出
  
  if (containerWidth < 400) {
    // 移动端：使用容器宽度减去 padding
    chartSize.value = Math.max(250, containerWidth - padding * 2)
  } else if (containerWidth < 600) {
    // 平板：中等尺寸
    chartSize.value = Math.min(350, containerWidth - padding * 2)
  } else {
    // 桌面端：使用原始尺寸或容器宽度
    chartSize.value = Math.min(props.size, containerWidth - padding * 2)
  }
}

onMounted(() => {
  // 延迟计算，确保容器已渲染
  setTimeout(() => {
    calculateSize()
  }, 100)
  
  // 监听窗口大小变化
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeTimer.value) {
    clearTimeout(resizeTimer.value)
  }
})

const resizeTimer = ref(null)
const handleResize = () => {
  if (resizeTimer.value) {
    clearTimeout(resizeTimer.value)
  }
  resizeTimer.value = setTimeout(() => {
    calculateSize()
  }, 300)
}

// 格式化分数显示（如果是0-1范围，转换为百分制）
const formatScore = (score) => {
  if (props.maxScore === 1) {
    // 归一化分数，转换为百分制显示
    return (score * 100).toFixed(0)
  } else {
    // 原始分数，直接显示
    return score.toFixed(1)
  }
}

// 中心点坐标
const centerX = computed(() => chartSize.value / 2)
const centerY = computed(() => chartSize.value / 2)
const radius = computed(() => chartSize.value / 2 - 60)

// 维度列表，带颜色和emoji
const dimensionList = computed(() => [
  { key: 'DOM', ...dimensions.DOM, color: '#ef4444', emoji: '👑' },
  { key: 'STR', ...dimensions.STR, color: '#8b5cf6', emoji: '🧠' },
  { key: 'COM', ...dimensions.COM, color: '#f59e0b', emoji: '🤝' },
  { key: 'SOL', ...dimensions.SOL, color: '#06b6d4', emoji: '🌙' },
  { key: 'AGI', ...dimensions.AGI, color: '#10b981', emoji: '⚡' },
  { key: 'SEC', ...dimensions.SEC, color: '#3b82f6', emoji: '🛡️' },
  { key: 'AES', ...dimensions.AES, color: '#ec4899', emoji: '🌸' }
])

// 计算角度（从顶部开始，顺时针）
const getAngle = (index) => {
  return (index * 2 * Math.PI / dimensionList.value.length) - Math.PI / 2
}

// 获取多边形点（用于网格）
const getPolygonPoints = (ratio) => {
  return dimensionList.value.map((_, index) => {
    const angle = getAngle(index)
    const x = centerX.value + radius.value * ratio * Math.cos(angle)
    const y = centerY.value + radius.value * ratio * Math.sin(angle)
    return `${x},${y}`
  }).join(' ')
}

// 获取轴线端点
const getPointX = (index, ratio) => {
  return centerX.value + radius.value * ratio * Math.cos(getAngle(index))
}

const getPointY = (index, ratio) => {
  return centerY.value + radius.value * ratio * Math.sin(getAngle(index))
}

// 获取标签位置（稍微外移，但确保不超出边界）
const getLabelX = (index) => {
  const labelRadius = radius.value + 40
  // 确保标签不超出 SVG 边界
  const x = centerX.value + labelRadius * Math.cos(getAngle(index))
  return Math.max(20, Math.min(chartSize.value - 20, x))
}

const getLabelY = (index) => {
  const labelRadius = radius.value + 40
  // 确保标签不超出 SVG 边界
  const y = centerY.value + labelRadius * Math.sin(getAngle(index))
  return Math.max(20, Math.min(chartSize.value - 20, y))
}

// 获取用户得分多边形
const getUserPolygonPoints = () => {
  return dimensionList.value.map((dim, index) => {
    const score = props.userScores[dim.key]
    const ratio = score / props.maxScore
    const angle = getAngle(index)
    const x = centerX.value + radius.value * ratio * Math.cos(angle)
    const y = centerY.value + radius.value * ratio * Math.sin(angle)
    return `${x},${y}`
  }).join(' ')
}

// 获取用户得分点位置
const getUserPointX = (index) => {
  const dim = dimensionList.value[index]
  const score = props.userScores[dim.key]
  const ratio = score / props.maxScore
  return centerX.value + radius.value * ratio * Math.cos(getAngle(index))
}

const getUserPointY = (index) => {
  const dim = dimensionList.value[index]
  const score = props.userScores[dim.key]
  const ratio = score / props.maxScore
  return centerY.value + radius.value * ratio * Math.sin(getAngle(index))
}
</script>

<style scoped>
.dimension-scores {
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #1a202c;
  margin: 0 0 2rem 0;
  text-align: center;
}

.radar-chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
  width: 100%;
  min-height: 300px;
  padding: 20px;
  overflow: visible;
}

.radar-chart {
  max-width: 100%;
  height: auto;
  width: 100%;
  max-height: 100%;
}

.dimension-label {
  font-size: 14px;
  font-weight: 600;
}

.dimension-score {
  font-size: 12px;
  fill: #4a5568;
}

.user-polygon {
  transition: all 0.3s ease;
}

.user-polygon:hover {
  opacity: 0.8;
}

.score-point circle {
  transition: all 0.2s ease;
  cursor: pointer;
}

.score-point:hover circle {
  r: 7;
}

.dimensions-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.dimension-item {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dimension-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.dimension-info {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
}

.dimension-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.dimension-text {
  flex: 1;
}

.dimension-name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.25rem;
}

.dimension-name-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a202c;
}

.dimension-score-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.dimension-description {
  font-size: 0.9rem;
  color: #4a5568;
  line-height: 1.5;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease, background-color 0.3s ease;
}

@media (max-width: 768px) {
  .dimension-scores {
    padding: 1.5rem;
  }

  .radar-chart-container {
    min-height: 250px;
    padding: 15px;
  }

  .radar-chart {
    width: 100%;
    height: auto;
  }

  .dimension-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .dimension-name-text {
    font-size: 1rem;
  }
  
  .dimension-label {
    font-size: 12px;
  }
  
  .dimension-score {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .radar-chart-container {
    min-height: 200px;
    padding: 10px;
  }
  
  .dimension-label {
    font-size: 11px;
  }
  
  .dimension-score {
    font-size: 9px;
  }
}
</style>

